import { Icon, IconButton, MenuItem } from "@material-ui/core";
import React, { Fragment, useEffect, useState } from "react";
import styled from "styled-components";
import { Trans } from "react-i18next";
import { LibraryView } from "common/constants";
import {
    ActionPermissionsObject,
    GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS,
    IWorkspacePermissionAccess,
    IWorkspaceResourceFolder,
    PermissionActionType,
    WorkspacePermissionSubjectType,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    WorkspaceUserRole,
    WorkspacePlanTier
} from "common/interfaces";
import PresentationLibraryController, {
    PresentationFilterType,
    PresentationLibraryFilter,
    PresentationLibraryPresentation
} from "js/controllers/PresentationLibraryController";
import WorkspaceController, {
    getUserPermissionsToResourceFolder,
    WorkspaceControllerState,
} from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import { openTeamUpgrade } from "js/core/utilities/externalLinks";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { app } from "js/namespaces";
import UpgradeIcon from "js/Components/UpgradeIcon";
import UpgradeSection from "js/Components/UpgradeSection";
import { UpgradeTooltip } from "js/Components/UpgradeTooltip";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowDialogAsync,
    ShowErrorDialog,
    ShowInputDialog,
    ShowSnackBar,
    ShowUpgradeDialog,
    ShowWarningDialog
} from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import { FlexSpacer, Gap20 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Divider, IconMenu } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import { getAddToFolderSnackbarProps } from "js/editor/PresentationLibrary/utils/getAddToFolderSnackbarProps";
import {
    AddPresentationsToFolderDialog,
    ManageTeamFolderMembersDialog,
} from "js/react/views/TeamResources/dialogs/TeamFolderDialogs";
import GuestWorkspaceDialog from "js/react/components/Dialogs/GuestWorkspaceDialog";
import { t } from "js/i18n";
import { Icon as BAIIcon } from "js/Components/Icon";
import { PresentationLibrarySideBarLayout } from "js/editor/PresentationLibrary/PresentationLibrarySideBarLayout";
import PresentationLibraryWorkspaceDropdown from "js/editor/PresentationLibrary/PresentationLibraryWorkspaceDropdown";

const logger = getLogger();

export const SideBarItem = styled.div`
    width: 100%;
    color: #444;
    font-size: 14px;
    letter-spacing: 0px;
    font-weight: 600;
    display: flex;
    flex-shrink: 0;
    margin-bottom: 0px;
    align-items: flex-start;
    overflow: hidden;
    padding: 10px;
    transition: background-color 300ms;
    background: none;
    cursor: pointer;
    color: #FFF;

    display: flex;
    flex-direction: row;
    gap: 10px;


    font-family: "Source Sans Pro";
    line-height: 150%;


    //.hover was added to fix a safari issue when hovering over a SideBarItem while dragging a presentation
    &:hover,
    &.hover {
        background: rgba(255, 255, 255, 0.10);
        border-radius: 2px;
        cursor: pointer;
    }

    &.selected {
        cursor: pointer;
        background: rgba(255, 255, 255, 0.15);

        &:hover {
            background: rgba(255, 255, 255, 0.10);
        }
    }

    .folder-name {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .dark-mode & {
        color: #ccc;
        background: none;

        & > .material-icons {
            color: #aaa;
        }

        //.hover was added to fix a safari issue when hovering over a SideBarItem while dragging a presentation
        &:hover {
            background: #555;
        }

        &.selected {
            background: #333;

            & > .material-icons {
                color: ${themeColors.ui_blue};
            }

            //.hover was added to fix a safari issue when hovering over a SideBarItem while dragging a presentation
            &:hover {
                background: #333;
            }
        }
    }
`;

const FolderContainer = styled.div`
  width: 100%;
  position: relative;
`;

const FolderLabel = styled.div`
    padding: 0 8px;
    font-family: "Source Sans Pro";
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 100%; /* 12px */
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #FFF;
`;

const SideBarLabel = styled.div`
    width: 100%;
    font-size: 12px;
    display: flex;
    align-items: center;
    text-transform: uppercase;

    color: #FFF;
    padding: 6px 0px;

    .dark-mode & {
        color: #bbb;
    }

    & > *:nth-child(3) {
        margin-left: auto;
        margin-right: 10px;
    }
`;

const LibraryFolder = styled(SideBarItem)`
    padding: 6px 0px;
    align-items: center;

    .MuiIconButton-root {
        padding: 0px 0px;
        opacity: 0;
    }

    .expand-arrow {
        font-size: 24px;
        margin-right: 0;
    }

    &:hover {
        .MuiIconButton-root {
            opacity: 1;
        }
    }
`;

const SubLibraryFolder = styled(LibraryFolder)`
    padding-left: 40px;
    width: calc(100% - 40px);

    &:hover {
        width: 100%;
        padding-left: 40px;
    }
`;

const StyledTeamFolders = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const SideBarContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    gap: 15px;
`;

const SideBarMainSection = styled(FlexBox)`
    flex: 1;
    min-height: 0;
`;

const TrashSideBarContainer = styled.div`
    width: 100%;
`;

const StyledAddIcon = styled(Icon)`
    &&& {
        font-size: 20px;
        color: #FFF;
    }
`;

const RestrictedMenuItem = styled(MenuItem)`
    &&& {
        color: ${themeColors.lighterGray};
    }
`;

// NOTE: We have to use `className="selected"` instead of `props.selected`
//   because a bug with `styled and` the application of `.dark-mode`
function selectedClass(selected: boolean) {
    return selected ? "selected" : "";
}

interface PresentationLibrarySideBarProps {
    presentations: PresentationLibraryPresentation[];
    readOnly: boolean;
    filter: PresentationLibraryFilter;
    canEditLibraryItems: boolean;
    draggedItems: PresentationLibraryPresentation[];
    onSetFilter: (filter: PresentationLibraryFilter) => void;
    onHideSidebar?: () => void;
    libraryView?: string;
    onSwitchType?: (view: string) => void;
    isMyPresentationsOnly?: boolean;
    showViewSelect?: boolean;
    isSideBarContentVisible?: boolean;
}

export const PresentationLibrarySideBar = WorkspaceController.withInitializedState(
    function PresentationLibrarySideBar(props: PresentationLibrarySideBarProps & WorkspaceControllerState) {
        const {
            readOnly,
            filter,
            onHideSidebar,
            canEditLibraryItems,
            workspace: { id: workspaceId },
            plan: { tier },
            onSetFilter,
            resourceFolders,
            draggedItems,
            presentations,
            libraryView,
            onSwitchType,
            isMyPresentationsOnly = false,
            showViewSelect = true,
            isSideBarContentVisible = true
        } = props;

        const [isPersonalFoldersExpanded, setIsPersonalFoldersExpanded] = useState(true);
        const [isTeamFoldersExpanded, setIsTeamFoldersExpanded] = useState(WorkspaceController.isMultiUserWorkspace);
        const isGuest = WorkspaceController.role === WorkspaceUserRole.GUEST;
        const canCreateFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
        const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
        const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
        const canUpgradeToCreateSharedResources = (tier !== WorkspacePlanTier.BASIC) && WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.CREATE);

        const userFolders = resourceFolders.filter(f => f.folderType === WorkspaceResourceFolderType.PERSONAL)
            .sort((a, b) => a.name.localeCompare(b.name));
        const teamFolders =
            [
                ...resourceFolders.filter(folder => !folder.parentFolderId && folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT),
                ...resourceFolders.filter(folder => !folder.parentFolderId && folder.folderType === WorkspaceResourceFolderType.SHARED)
                    .sort((a, b) => a.name.localeCompare(b.name))
            ];

        // team accounts should have a shared with me folder EVEN IF there is nothing shared with them yet - the page should just show an empty state
        const hasSharedWithMePresentations = presentations.filter(presentation => !presentation.permissions.owner && !presentation.trashedAt).length > 0;
        const hasCreatedByMePresentations = presentations.filter(presentation => presentation.permissions.owner && !presentation.trashedAt).length > 0;
        const showPersonalFoldersSection = true;
        const showTeamFoldersSection = true;
        const showAnyFoldersSection = true;

        const handleSelectFilter = (filterType: PresentationFilterType) => {
            onSetFilter({ type: filterType });
            onHideSidebar?.();
        };

        const handleTrashMouseUp = () => {
            if (draggedItems.length > 0) {
                PresentationLibraryController.trashPresentations(draggedItems.map(p => p.id));
                return;
            }

            onSetFilter({ type: PresentationFilterType.TRASH });
        };

        const handleCreateFolder = async (folderType: WorkspaceResourceFolderType) => {
            setIsPersonalFoldersExpanded(true);

            if (!WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create) {
                if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE)) {
                    ShowUpgradeDialog({
                        type: UpgradePlanDialogType.UPGRADE_PLAN,
                        analytics: { cta: "Folder" },
                        workspaceId
                    });
                    return;
                }

                ShowWarningDialog({
                    title: t("Oops!"),
                    message: t("You do not have permission to create folders!")
                });
                return;
            }

            // Legacy limit
            if (folderType === WorkspaceResourceFolderType.PERSONAL && userFolders.length > 49) {
                ShowWarningDialog({
                    title: t("Oops!"),
                    message: t("You have reached the folder limit!")
                });
                return;
            }

            const folderName: string = await new Promise(resolve => ShowDialog(InputConfirmationDialog, {
                title: folderType === WorkspaceResourceFolderType.PERSONAL ? t("Create Folder") : t("Create Team Folder"),
                input: {
                    type: "text",
                    placeholder: t("Type folder name..."),
                    id: "create_folder_input",
                    inputProps: {
                        maxLength: 50
                    }
                },
                acceptCallback: (input: string) => resolve(input.trim())
            }));

            if (resourceFolders.some(folder => folder.name === folderName)) {
                ShowWarningDialog({
                    title: t("Unable to create folder"),
                    message: t("Please enter a unique folder name.")
                });
                return;
            }

            try {
                const folder = await withProgressDialog({
                    title: folderType === WorkspaceResourceFolderType.PERSONAL ? t("Creating folder...") : t("Creating team folder..."),
                    action: () => WorkspaceController.createWorkspaceResourceFolder({ name: folderName, resourcesType: WorkspaceResourceType.PRESENTATION, folderType })
                });

                if (folderType === WorkspaceResourceFolderType.SHARED) {
                    ShowDialog(ManageTeamFolderMembersDialog, {
                        folderId: folder.id
                    });
                }
            } catch (err) {
                logger.error(err, "[PresentationLibrarySideBar] handleCreateFolder() Unable to create folder");

                ShowErrorDialog({
                    title: t("Unable to create folder"),
                    message: err.message
                });
            }
        };

        const sideBarContent = (
            <SideBarContentContainer>
                <PresentationLibraryWorkspaceDropdown workspaceId={workspaceId} />
                {/* @ts-ignore */}
                <SideBarMainSection column
                    className="library-sidebar"
                    verticalAlign="start"
                    horizontalAlign="start"
                    $canEditLibraryItems={canEditLibraryItems}
                >
                    {!isGuest && <SideBarItem
                        className={selectedClass(filter.type == PresentationFilterType.ALL_PRESENTATIONS)}
                        onClick={() => handleSelectFilter(PresentationFilterType.ALL_PRESENTATIONS)}
                        id="all-presentations"
                    >
                        <BAIIcon size={20} fill={filter.type == PresentationFilterType.ALL_PRESENTATIONS} color="#FFF">stacks</BAIIcon>
                        {t("All Presentations")}
                    </SideBarItem>
                    }
                    <SideBarItem
                        className={selectedClass(filter.type == PresentationFilterType.RECENT)}
                        onClick={() => handleSelectFilter(PresentationFilterType.RECENT)}
                        id="recent-presentations"
                    >
                        <BAIIcon size={20} fill={filter.type == PresentationFilterType.RECENT} color="#FFF">schedule</BAIIcon>
                        {t("Recent Presentations")}
                    </SideBarItem>
                    {WorkspaceController.isMultiUserWorkspace &&
                        <SideBarItem
                            className={selectedClass(filter.type == PresentationFilterType.OWNED_BY_ME)}
                            onClick={() => handleSelectFilter(PresentationFilterType.OWNED_BY_ME)}
                            id="owned-by-me"
                        >
                            <BAIIcon size={20} fill={filter.type == PresentationFilterType.OWNED_BY_ME} color="#FFF">person</BAIIcon>
                            {t("Created By Me")}
                        </SideBarItem>
                    }
                    {(isGuest || WorkspaceController.isMultiUserWorkspace) &&
                        <SideBarItem
                            className={selectedClass(filter.type == PresentationFilterType.SHARED_WITH_ME)}
                            onClick={() => handleSelectFilter(PresentationFilterType.SHARED_WITH_ME)}
                            id="shared-with-me"
                        >
                            <BAIIcon size={20} fill={filter.type == PresentationFilterType.SHARED_WITH_ME} color="#FFF">group</BAIIcon>
                            {t("Shared With Me")}
                        </SideBarItem>
                    }
                    {/* @ts-ignore */}
                    {showAnyFoldersSection && <Divider margin={16} color="#666" />}
                    <StyledTeamFolders>
                        {/* User Folders */}
                        {showPersonalFoldersSection && <>
                            <SideBarLabel>
                                <Icon
                                    onMouseUp={() => setIsPersonalFoldersExpanded(!isPersonalFoldersExpanded)}
                                    className="expand-arrow">{isPersonalFoldersExpanded ? "arrow_drop_down" : "arrow_right"}
                                </Icon>
                                <FolderLabel>{t("My Folders")}</FolderLabel>
                                {!readOnly && !app.isConstrained && canCreateFolders &&
                                    <IconButton
                                        size="small"
                                        id="create-folder-button"
                                        onClick={() => handleCreateFolder(WorkspaceResourceFolderType.PERSONAL)}
                                    >
                                        <StyledAddIcon>add</StyledAddIcon>
                                    </IconButton>
                                }
                                {!canCreateFolders &&
                                    <UpgradeTooltip
                                        tooltipTitle="Unlock My Folders"
                                        tooltipText="Upgrade to keep your presentations organized and easy to find."
                                        onUpgrade={() => ShowDialog(ChangePlanDialog, {
                                            featuredPlan: WorkspacePlanTier.PRO,
                                            analytics: { cta: "unlock_my_folders" }
                                        })}
                                    >
                                        <UpgradeIcon />
                                    </UpgradeTooltip>
                                }
                                {/* Uncomment when we expose new basic user experience
                                canUpgradeToCreateFolders && !isGuest &&
                                    <UpgradeTooltip
                                        tooltipTitle="Unlock Presentation Folders"
                                        tooltipText="Upgrade your plan to organize your presentations into folders."
                                        onUpgrade={() => ShowDialog(ChangePlanDialog, { featuredPlan: WorkspacePlanTier.PRO, analytics: { cta: "FUTURE_TODO" } })}
                                    >
                                        <img src={<UpgradeIcon />}/>
                                    </UpgradeTooltip>
                                */}
                            </SideBarLabel>
                            {isPersonalFoldersExpanded && <FolderContainer>
                                {userFolders.map((userFolder, idx) => (
                                    <UserFolder
                                        key={idx}
                                        folder={userFolder}
                                        readOnly={readOnly}
                                        onSetFilter={onSetFilter}
                                        draggedItems={draggedItems}
                                        filter={filter}
                                        onHideSidebar={onHideSidebar}
                                    />
                                ))}
                            </FolderContainer>}
                        </>}
                        {/* Team Folders */}
                        {showTeamFoldersSection && <>
                            <SideBarLabel>
                                {<Icon
                                    onMouseUp={() => setIsTeamFoldersExpanded(!isTeamFoldersExpanded)}
                                    className="expand-arrow">{isTeamFoldersExpanded ? "arrow_drop_down" : "arrow_right"}
                                </Icon>}
                                <FolderLabel>{t("Team Folders")}</FolderLabel>
                                {
                                    !readOnly && !app.isConstrained && canCreateFolders && canUseSharedResources &&
                                    <IconButton
                                        size="small"
                                        id="create-folder-button"
                                        onClick={() => handleCreateFolder(WorkspaceResourceFolderType.SHARED)}
                                    >
                                        <StyledAddIcon>add</StyledAddIcon>
                                    </IconButton>
                                }
                                {(canUpgradeToCreateSharedResources || !canCreateFolders) &&
                                    <UpgradeTooltip
                                        tooltipTitle="Unlock Team Folders"
                                        tooltipText="Upgrade to a Team plan to keep your team’s presentations organized and easy to find."
                                        onUpgrade={() => ShowDialog(ChangePlanDialog, {
                                            featuredPlan: WorkspacePlanTier.TEAM,
                                            analytics: { cta: "unlock_team_folders" }
                                        })}
                                    >
                                        <UpgradeIcon />
                                    </UpgradeTooltip>
                                }
                            </SideBarLabel>
                            {isTeamFoldersExpanded && <FolderContainer>
                                {teamFolders.map((folder, idx) => (
                                    <TeamFolder
                                        key={idx}
                                        folder={folder}
                                        readOnly={readOnly}
                                        onSetFilter={onSetFilter}
                                        draggedItems={draggedItems}
                                        filter={filter}
                                        onHideSidebar={onHideSidebar}
                                    />
                                ))}
                            </FolderContainer>}
                            <Gap20 />
                        </>}

                    </StyledTeamFolders>
                </SideBarMainSection>

                {!readOnly && !app.isConstrained &&
                    <TrashSideBarContainer>
                        <Divider margin={10} color="#666" />
                        <SideBarItem
                            className={selectedClass(filter.type == PresentationFilterType.TRASH)}
                            onMouseUp={handleTrashMouseUp}
                            id="trash"
                        >
                            <BAIIcon size={20} fill={filter.type == PresentationFilterType.TRASH} color="#FFF">delete</BAIIcon>
                            {t("Trash")}
                        </SideBarItem>
                    </TrashSideBarContainer>
                }

                {isGuest &&
                    <>
                        <Divider margin={10} color="#666" />
                        <UpgradeSection
                            darkMode
                            title={t("You're a guest")}
                            body={t("As a guest you can collaborate, but can't create new presentations. To unlock all of Beautiful.ai's features, create a workspace.")}
                            buttonText={t("Create a workspace")}
                            buttonIcon={<UpgradeIcon />}
                            buttonOnClick={openTeamUpgrade}
                        />
                    </>
                }
            </SideBarContentContainer>
        );

        if (!libraryView || !onSwitchType) {
            return sideBarContent;
        }

        return (
            <PresentationLibrarySideBarLayout
                libraryView={libraryView}
                onSwitchType={onSwitchType}
                isMyPresentationsOnly={isMyPresentationsOnly}
                showViewSelect={showViewSelect}
                isSideBarContentVisible={isSideBarContentVisible}
            >
                {sideBarContent}
            </PresentationLibrarySideBarLayout>
        );
    }
);

interface FolderProps {
    folder: IWorkspaceResourceFolder;
    readOnly: boolean;
    onSetFilter: (filter: PresentationLibraryFilter) => void;
    draggedItems: PresentationLibraryPresentation[];
    filter: PresentationLibraryFilter;
    onHideSidebar?: () => void;
}

const UserFolder = WorkspaceController.withInitializedState(function UserFolder(props: FolderProps & WorkspaceControllerState) {
    const { folder, readOnly, onSetFilter, onHideSidebar, resourceFolders, resources, draggedItems, filter } = props;
    const handleRenameFolder = async () => {
        let name = await ShowInputDialog({
            title: t("Rename Folder"),
            maxChars: 50,
            value: folder.name
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (folder.name === name) {
            return;
        }

        try {
            if (resourceFolders.some(folder => folder.name === name)) {
                throw new Error(t("Please enter a unique folder name."));
            }

            await withProgressDialog({
                title: t("Renaming folder..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name })
            });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][UserFolder] handleRenameFolder() Unable to rename folder");

            ShowErrorDialog({
                title: t("Unable to rename folder"),
                message: err.message
            });
        }
    };

    const handleDeleteFolder = async () => {
        try {
            if (resources.some(resource => resource.folderIds?.includes(folder.id))) {
                throw new Error(t("This folder contains presentations and can't be deleted."));
            }

            await withProgressDialog({
                title: t("Deleting folder..."),
                action: () => WorkspaceController.deleteWorkspaceResourceFolder({ folderId: folder.id })
            });

            onSetFilter({ type: PresentationFilterType.ALL_PRESENTATIONS });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][UserFolder] handleDeleteFolder() Unable to delete folder");

            ShowErrorDialog({
                title: t("Unable to delete folder"),
                message: err.message
            });
        }
    };

    const handleMouseUp = async (e: React.MouseEvent<HTMLDivElement>) => {
        if (draggedItems.length === 0) {
            e.stopPropagation();
            e.preventDefault();
            onSetFilter({ type: PresentationFilterType.FOLDER, folderId: folder.id });
            onHideSidebar?.();
            return;
        }

        try {
            // Recover any presentations that are in the trash before adding them to a folder.
            if (draggedItems.some(item => item.trashedAt)) {
                await PresentationLibraryController.recoverPresentations(draggedItems.filter(item => item.trashedAt).map(item => item.id));
            }

            await withProgressDialog({
                title: t("Moving presentations..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolderResources({
                    folderId: folder.id,
                    update: {
                        add: draggedItems.map(item => ({
                            resourceId: item.id,
                            resourceType: WorkspaceResourceType.PRESENTATION,
                            permissions: { read: { object: true, permissions: true }, write: { object: true, permissions: true } }
                        }))
                    }
                })
            });

            let snackbar;
            const handleClick = () => {
                onSetFilter({ type: PresentationFilterType.FOLDER, folderId: folder.id });
                snackbar.close();
            };
            const { i18nKey, values } = getAddToFolderSnackbarProps(draggedItems.length, folder.name);
            snackbar = ShowSnackBar({
                message: (
                    <p>
                        <Trans
                            t={t}
                            i18nKey={i18nKey}
                            values={values}
                            components={{ folderLink: <span className="action_item" onClick={handleClick} /> }}
                        />
                    </p>
                ),
            });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][UserFolder] handleMouseUp() Unable to move presentations into folder");

            ShowErrorDialog({
                title: t("Unable to move presentations into folder"),
                message: err.message
            });
        }
    };

    const handleCanUpgradeAction = cta => () => {
        if (WorkspaceController.role === WorkspaceUserRole.GUEST) {
            ShowDialog(GuestWorkspaceDialog);
        } else {
            ShowDialog(ChangePlanDialog, { analytics: { cta } });
        }
    };

    const canRenameUserFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const canUpgradeToRenameUserFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE);
    const canDeleteUserFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const canUpgradeToDeleteUserFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE);

    return (
        <LibraryFolder
            data-attr={folder.name}
            className={selectedClass(filter.type == PresentationFilterType.FOLDER && filter.folderId == folder.id)}
            onMouseUp={handleMouseUp}
            onTouchEnd={handleMouseUp}
        >
            <Icon className="expand-arrow" />
            <BAIIcon size={20} fill={filter.type == PresentationFilterType.FOLDER && filter.folderId == folder.id} color="#FFF">folder</BAIIcon>
            <div className="folder-name"> {folder.name}</div>
            <FlexSpacer />
            {!readOnly && !app.isConstrained &&
                <IconMenu>
                    {canRenameUserFolders &&
                        <MenuItem onClick={handleRenameFolder}>
                            <Icon>label</Icon>
                            {t("Rename Folder")}
                        </MenuItem>}
                    {canUpgradeToRenameUserFolders && (
                        <RestrictedMenuItem divider onClick={handleCanUpgradeAction("rename_folder")}>
                            <UpgradeIcon style={{ marginRight: 8 }} />
                            {t("Rename Folder")}
                        </RestrictedMenuItem>
                    )}

                    <hr />
                    {canDeleteUserFolders &&
                        <MenuItem onClick={handleDeleteFolder}>
                            <Icon>delete</Icon>
                            {t("Delete Folder")}
                        </MenuItem>}

                    {canUpgradeToDeleteUserFolders &&
                        <RestrictedMenuItem onClick={handleCanUpgradeAction("delete_folder")}>
                            <UpgradeIcon style={{ marginRight: 8 }} />
                            {t("Delete Folder")}
                        </RestrictedMenuItem>}
                </IconMenu>
            }
        </LibraryFolder>
    );
});

const TeamFolder = WorkspaceController.withInitializedState(function TeamFolder(props: FolderProps & WorkspaceControllerState) {
    const { folder, workspace: { id: workspaceId }, plan: { tier }, onHideSidebar, filter, readOnly, onSetFilter, draggedItems, resourceFolders, resources } = props;
    const [permissions, setPermissions] = useState<IWorkspacePermissionAccess>(GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS());
    const [isExpanded, setIsExpanded] = useState(false);

    const isDefault = folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT;
    const subFolders = resourceFolders.filter(f => f.parentFolderId === folder.id).sort((a, b) => a.name.localeCompare(b.name));

    const canUpgradeToCreateResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCES, PermissionActionType.CREATE);

    useEffect(() => {
        (async () => {
            try {
                const fetchedPermissions = await getUserPermissionsToResourceFolder({
                    workspaceId: workspaceId,
                    folderId: folder.id,
                });
                setPermissions(fetchedPermissions);
            } catch (err) {
                logger.error(err, "[PresentationLibrarySideBar][TeamFolder] useEffect() Unable to fetch permissions");
            }
        })();
    }, [folder.id]);

    const handleMouseUp = async () => {
        if (draggedItems.length === 0) {
            onSetFilter({ type: PresentationFilterType.TEAM_FOLDER, folderId: folder.id });
            if (subFolders.length === 0) {
                onHideSidebar?.();
            }
            return;
        }

        try {
            const { write } = await getUserPermissionsToResourceFolder({
                workspaceId,
                folderId: folder.id,
            });
            if (!write.object) {
                ShowWarningDialog({
                    title: t("Permission denied"),
                    message: t("You don't have permissions to add presentations to this team folder. Contact your Team Admin to request access.")
                });
                return;
            }

            const subFolders = resourceFolders.filter(f => f.parentFolderId === folder.id);
            const presentationsNotInFolder = draggedItems.map(i => resources.find(r => r.resourceId === i.id)).filter(r => !r.folderIds?.includes(folder.id) && !r.folderIds?.some(id => subFolders.some(f => f.id === id)));
            if (presentationsNotInFolder.length > 0) {
                ShowDialog(AddPresentationsToFolderDialog, {
                    folderId: folder.id,
                    presentationIds: draggedItems.map(i => i.id),
                });
            } else {
                await withProgressDialog({
                    title: t("Moving presentations..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderResources({
                        folderId: folder.id,
                        update: {
                            add: draggedItems.map(i => ({
                                resourceId: i.id,
                                resourceType: WorkspaceResourceType.PRESENTATION,
                                permissions: { read: { object: true, permissions: true }, write: { object: true, permissions: true } }
                            }))
                        }
                    })
                });
            }
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][TeamFolder] handleMouseUp() Unable to move presentations into folder");

            ShowErrorDialog({
                title: t("Unable to move presentations into folder"),
                message: err.message
            });
        }
    };

    const handleEditSharing = () => {
        if (isDefault) {
            ShowWarningDialog({
                title: t("Sharing can't be modified"),
                message: t("The All Team Members folder is shared with your entire team and can't be modified or deleted.")
            });
            return;
        }

        ShowDialog(ManageTeamFolderMembersDialog, {
            folderId: folder.id
        });
    };

    const handleRenameFolder = async () => {
        let name = await ShowInputDialog({
            title: t("Rename Folder"),
            maxChars: 50,
            value: folder.name
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (folder.name === name) {
            return;
        }

        try {
            if (resourceFolders.some(folder => folder.name === name)) {
                throw new Error(t("Please enter a unique folder name."));
            }

            await withProgressDialog({
                title: t("Renaming folder..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name })
            });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][UserFolder] handleRenameFolder() Unable to rename folder");

            ShowErrorDialog({
                title: t("Unable to rename folder"),
                message: err.message
            });
        }
    };

    const handleCreateSubFolder = async () => {
        const name = await ShowInputDialog({
            title: t("New folder"),
            maxChars: 100,
            value: t("New Folder")
        });

        if (!name) {
            return;
        }

        try {
            await withProgressDialog({
                title: t("Creating subfolder..."),
                action: () => WorkspaceController.createWorkspaceResourceFolder({
                    parentFolderId: folder.id,
                    name,
                    resourcesType: WorkspaceResourceType.PRESENTATION,
                    folderType: WorkspaceResourceFolderType.SHARED
                })
            });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][TeamFolder] handleCreateSubFolder() Unable to create subfolder");

            ShowErrorDialog({
                title: t("Unable to create subfolder"),
                message: err.message
            });
        }
    };

    const handleDeleteFolder = async () => {
        if (await ShowConfirmationDialog({
            title: t("Delete this shared team folder?"),
            message: <Fragment>
                <p>{t("Presentations in the folder will NOT be deleted, but members may lose access.")}</p>
                <br />
                <p>{t("You cannot undo this action.")}</p>
            </Fragment>
        })) {
            try {
                await withProgressDialog({
                    title: t("Deleting folder..."),
                    action: () => WorkspaceController.deleteWorkspaceResourceFolder({ folderId: folder.id })
                });

                onSetFilter({ type: PresentationFilterType.ALL_PRESENTATIONS });
            } catch (err) {
                if (err) {
                    logger.error(err, "[PresentationLibrarySideBar][TeamFolder] handleDeleteFolder() failed", { folderId: folder.id });

                    ShowErrorDialog({
                        error: t("Failed to delete team folder"),
                        message: t("Something went wrong. Try again or contact support@beautiful.ai if the issue persists.")
                    });
                }
            }
        }
    };

    const handleLeaveFolder = async () => {
        if (await ShowConfirmationDialog({
            title: t("Leave this team folder?"),
            message: t("You may lose access to presentations in this folder. You can rejoin only if a team owner or librarian invites you.")
        })) {
            onSetFilter({ type: PresentationFilterType.ALL_PRESENTATIONS });

            try {
                await withProgressDialog({
                    title: t("Leaving team folder..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderUsers({
                        folderId: folder.id,
                        update: { remove: [{ subjectId: app.user.id, subjectType: WorkspacePermissionSubjectType.USER }] }
                    })
                });
            } catch (err) {
                logger.error(err, "[PresentationLibrarySideBar][TeamFolder] handleLeaveFolder() Unable to leave team folder");

                ShowErrorDialog({
                    title: t("Unable to leave team folder"),
                    message: err.message
                });
            }
        }
    };

    const handleCanUpgradeAction = () => {
        if (WorkspaceController.role === WorkspaceUserRole.GUEST) {
            ShowDialog(GuestWorkspaceDialog);
        } else {
            ShowDialog(ChangePlanDialog);
        }
    };

    // TODO: Extract the possible action permission for better reuse
    const canUpgradeToManageSharedResources = tier !== WorkspacePlanTier.BASIC && WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
    const canUpgradeToRenameTeamFolder = canUpgradeToManageSharedResources;
    const canRenameTeamFolder = !canUpgradeToRenameTeamFolder && permissions.write.object && !isDefault;
    const canUpgradeToCreateSubfolder = canUpgradeToManageSharedResources;
    const canCreateSubfolder = !canUpgradeToCreateSubfolder && permissions.write.object;

    return (
        <Fragment>
            <LibraryFolder
                data-attr={folder.name}
                data-attr-id={folder.id}
                className={selectedClass(filter.type == PresentationFilterType.TEAM_FOLDER && filter.folderId == folder.id && !filter.subFolderId)}
                onMouseUp={handleMouseUp}
                onTouchEnd={handleMouseUp}
            >
                {subFolders.length > 0
                    ? <Icon onMouseUp={() => setIsExpanded(!isExpanded)} className="expand-arrow">{isExpanded ? "arrow_drop_down" : "arrow_right"}</Icon>
                    : <Icon className="expand-arrow" />
                }
                <BAIIcon size={20} fill={filter.type == PresentationFilterType.TEAM_FOLDER && filter.folderId == folder.id && !filter.subFolderId} color="#FFF">folder_shared</BAIIcon>
                <div className="folder-name">
                    {folder.name}
                </div>
                <FlexSpacer />
                {!readOnly && !app.isConstrained &&
                    <IconMenu>
                        {!isDefault && !canUpgradeToManageSharedResources && (
                            <MenuItem divider onClick={handleEditSharing}>
                                <Icon>people_add</Icon>
                                {permissions.write.permissions ? t("Share Team Folder...") : t("View Sharing...")}
                            </MenuItem>
                        )}
                        {canUpgradeToManageSharedResources && (
                            <RestrictedMenuItem divider onClick={handleCanUpgradeAction}>
                                <UpgradeIcon style={{ marginRight: 8 }} />
                                {t("Share Team Folder...")}
                            </RestrictedMenuItem>
                        )}
                        {canRenameTeamFolder && (
                            <MenuItem divider onClick={handleRenameFolder}>
                                <Icon>label</Icon>
                                {t("Rename Team Folder...")}
                            </MenuItem>
                        )}
                        {canUpgradeToRenameTeamFolder && (
                            <RestrictedMenuItem divider onClick={handleCanUpgradeAction}>
                                <UpgradeIcon style={{ marginRight: 8 }} />
                                {t("Rename Team Folder...")}
                            </RestrictedMenuItem>
                        )}
                        {canCreateSubfolder && (
                            <MenuItem divider onClick={handleCreateSubFolder}>
                                <Icon>folder_open</Icon>
                                {t("Create SubFolder...")}
                            </MenuItem>
                        )}
                        {canUpgradeToCreateSubfolder && (
                            <RestrictedMenuItem divider onClick={handleCanUpgradeAction}>
                                <UpgradeIcon style={{ marginRight: 8 }} />
                                {t("Create SubFolder...")}
                            </RestrictedMenuItem>
                        )}
                        {permissions.owner && !isDefault && (
                            <MenuItem onClick={handleDeleteFolder}>
                                <Icon>delete</Icon>
                                {t("Delete Team Folder")}
                            </MenuItem>
                        )}
                        {!permissions.owner && !isDefault && (
                            <MenuItem onClick={handleLeaveFolder}>
                                <Icon>logout</Icon>
                                {t("Leave Team Folder")}
                            </MenuItem>
                        )}
                    </IconMenu>
                }
            </LibraryFolder>
            {isExpanded &&
                subFolders.map(subFolder => (
                    <TeamSubFolder
                        key={subFolder.id}
                        folder={subFolder}
                        readOnly={readOnly}
                        filter={filter}
                        onSetFilter={onSetFilter}
                        draggedItems={draggedItems}
                        permissions={permissions}
                        onHideSidebar={onHideSidebar}
                    />
                ))}
        </Fragment >
    );
});

const TeamSubFolder = WorkspaceController.withInitializedState(function TeamSubFolder(props: FolderProps & WorkspaceControllerState & { permissions: IWorkspacePermissionAccess }) {
    const { folder, readOnly, filter, onHideSidebar, onSetFilter, permissions, resourceFolders, resources, draggedItems, workspace: { id: workspaceId } } = props;
    const canUpgradeToCreateResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCES, PermissionActionType.CREATE);

    const handleMouseUp = async () => {
        if (draggedItems.length === 0) {
            onSetFilter({ type: PresentationFilterType.TEAM_FOLDER, folderId: folder.parentFolderId, subFolderId: folder.id });
            onHideSidebar?.();
            return;
        }

        try {
            const { write } = await getUserPermissionsToResourceFolder({
                workspaceId,
                folderId: folder.id
            });
            if (!write.object) {
                ShowWarningDialog({
                    title: t("Permission denied"),
                    message: t("You don't have permissions to add presentations to this team folder. Contact your Team Admin to request access.")
                });
                return;
            }

            const parentFolderAndItsSubfolderIds = [folder.parentFolderId, ...resourceFolders.filter(f => f.parentFolderId === folder.parentFolderId).map(f => f.id)];
            const draggedResources = draggedItems.map(i => resources.find(r => r.resourceId === i.id)).filter(Boolean);
            const draggedResourcesNotInParentFolder = draggedResources.filter(r => !r.folderIds?.some(id => parentFolderAndItsSubfolderIds.includes(id)));
            if (draggedResourcesNotInParentFolder.length > 0) {
                await ShowDialogAsync(AddPresentationsToFolderDialog, {
                    folderId: folder.id,
                    presentationIds: draggedResources.map(r => r.resourceId)
                });
            } else {
                await withProgressDialog({
                    title: t("Moving presentations..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderResources({
                        folderId: folder.id,
                        update: {
                            add: draggedResources.map(r => ({
                                resourceId: r.resourceId,
                                resourceType: WorkspaceResourceType.PRESENTATION,
                                permissions: { read: { object: true, permissions: true }, write: { object: true, permissions: true } }
                            }))
                        }
                    })
                });
            }
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][TeamFolder] handleMouseUp() Unable to move presentations into folder");

            ShowErrorDialog({
                title: t("Unable to move presentations into folder"),
                message: err.message
            });
        }
    };

    const handleRenameFolder = async () => {
        const name = await ShowInputDialog({
            title: t("Rename Subfolder"),
            maxChars: 50,
            value: folder.name
        });

        if (!name || folder.name === name) {
            return;
        }

        try {
            if (resourceFolders.some(folder => folder.name === name)) {
                throw new Error(t("Please enter a unique subfolder name."));
            }

            await withProgressDialog({
                title: t("Renaming subfolder..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name })
            });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][TeamSubFolder] handleRenameFolder() Unable to rename subfolder");

            ShowErrorDialog({
                title: t("Unable to rename subfolder"),
                message: err.message
            });
        }
    };

    const handleDeleteFolder = async () => {
        try {
            const resourcesInFolder = resources.filter(r => r.folderIds?.includes(folder.id));
            if (resourcesInFolder.length > 0) {
                if (!await ShowConfirmationDialog({
                    title: t("Delete this subfolder?"),
                    message: <p>{t("Presentations will move back to the root team folder and will not be deleted.")}</p>
                })) {
                    return;
                }
            }

            await withProgressDialog({
                title: t("Deleting subfolder..."),
                action: () => WorkspaceController.deleteWorkspaceResourceFolder({ folderId: folder.id })
            });

            onSetFilter({ type: PresentationFilterType.TEAM_FOLDER, folderId: folder.parentFolderId });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][TeamSubFolder] handleDeleteFolder() Unable to delete subfolder");

            ShowErrorDialog({
                title: t("Unable to delete subfolder"),
                message: err.message
            });
        }
    };

    return (
        <SubLibraryFolder
            data-attr={folder.name}
            className={selectedClass(filter.type == PresentationFilterType.TEAM_FOLDER && filter.subFolderId == folder.id)}
            onMouseUp={handleMouseUp}
            onTouchEnd={handleMouseUp}
        >
            <BAIIcon size={20} fill={filter.type == PresentationFilterType.TEAM_FOLDER && filter.subFolderId == folder.id} color="#FFF">folder_open</BAIIcon>
            <div className="folder-name">{folder.name}</div>

            <FlexSpacer />

            {!readOnly && !app.isConstrained && permissions.write.object &&
                <IconMenu>
                    <MenuItem onClick={handleRenameFolder}>
                        <Icon>label</Icon>
                        {t("Rename Folder")}
                    </MenuItem>
                    <hr />
                    <MenuItem onClick={handleDeleteFolder}>
                        <Icon>delete</Icon>
                        {t("Delete Folder")}
                    </MenuItem>
                </IconMenu>
            }
            {canUpgradeToCreateResources &&
                <IconMenu>
                    <RestrictedMenuItem onClick={() => ShowDialog(GuestWorkspaceDialog)}>
                        <UpgradeIcon style={{ marginRight: 8 }} />
                        {t("Rename Folder")}
                    </RestrictedMenuItem>
                    <hr />
                    <RestrictedMenuItem onClick={() => ShowDialog(GuestWorkspaceDialog)}>
                        <UpgradeIcon style={{ marginRight: 8 }} />
                        {t("Delete Folder")}
                    </RestrictedMenuItem>
                </IconMenu>
            }
        </SubLibraryFolder>
    );
});
